import axios, { AxiosError } from "axios";

const productionApiBaseUrl = "https://lenses-backend.onrender.com";

const getApiBaseUrl = () => {
  const configuredUrl = process.env.REACT_APP_API_URL?.trim();
  if (configuredUrl) {
    return configuredUrl;
  }

  if (
    typeof window !== "undefined" &&
    window.location.hostname !== "localhost" &&
    window.location.hostname !== "127.0.0.1"
  ) {
    return productionApiBaseUrl;
  }

  return "http://localhost:8000";
};

const apiBaseUrl = getApiBaseUrl();

const api = axios.create({
  baseURL: apiBaseUrl,
  timeout: 60000,
});

api.interceptors.request.use((config) => {
  const token =
    typeof window === "undefined" ? "" : window.localStorage.getItem("lenses.authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const retryableMethods = new Set(["get", "head", "options"]);
const retryableStatuses = new Set([408, 429, 500, 502, 503, 504]);

const wait = (delayMs: number) =>
  new Promise((resolve) => window.setTimeout(resolve, delayMs));

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as
      | (NonNullable<typeof error.config> & { __retryCount?: number })
      | undefined;
    const method = config?.method?.toLowerCase() ?? "get";

    if (!config || !retryableMethods.has(method)) {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const shouldRetry = status === undefined || retryableStatuses.has(status);
    const retryCount = config.__retryCount ?? 0;

    if (!shouldRetry || retryCount >= 2) {
      return Promise.reject(error);
    }

    config.__retryCount = retryCount + 1;
    await wait(1000 * config.__retryCount);
    return api(config);
  }
);

export default api;
